// ชิ้นส่วนหน้าจอพื้นฐาน — ปุ่มใหญ่ (สูง ≥ 48px) ตัวหนังสือใหญ่ สีตามความหมาย
import { useEffect, useId, useRef, useState, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';
import { Loader2, X } from 'lucide-react';
import type { HoldReason, Stage } from '@setcom/core';
import { HOLD_INFO, STAGE_INFO, PRIORITY_INFO, type Priority } from '@setcom/core';
import { DateInput, TimeInput } from './DateTime';

export const cx = (...c: (string | false | null | undefined)[]) => c.filter(Boolean).join(' ');

// ---------------- สีตามขั้นงาน
export const TONE: Record<string, { bg: string; soft: string; text: string; border: string }> = {
  sky: { bg: 'bg-st-sky', soft: 'bg-st-sky-soft', text: 'text-st-sky', border: 'border-st-sky' },
  violet: { bg: 'bg-st-violet', soft: 'bg-st-violet-soft', text: 'text-st-violet', border: 'border-st-violet' },
  amber: { bg: 'bg-st-amber', soft: 'bg-st-amber-soft', text: 'text-[#9a6b00]', border: 'border-st-amber' },
  orange: { bg: 'bg-st-orange', soft: 'bg-st-orange-soft', text: 'text-st-orange', border: 'border-st-orange' },
  blue: { bg: 'bg-st-blue', soft: 'bg-st-blue-soft', text: 'text-st-blue', border: 'border-st-blue' },
  green: { bg: 'bg-st-green', soft: 'bg-st-green-soft', text: 'text-st-green', border: 'border-st-green' },
  slate: { bg: 'bg-st-slate', soft: 'bg-st-slate-soft', text: 'text-st-slate', border: 'border-st-slate' },
  rose: { bg: 'bg-st-rose', soft: 'bg-st-rose-soft', text: 'text-st-rose', border: 'border-st-rose' },
};
export const toneOf = (color: string | null | undefined) => TONE[color ?? 'blue'] ?? TONE.blue!;

// ---------------- ปุ่ม
type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'success' | 'warning';
const VARIANT: Record<Variant, string> = {
  primary: 'bg-brand text-white hover:bg-brand-dark shadow-sm',
  secondary: 'bg-white text-ink border-2 border-line hover:border-brand hover:text-brand',
  ghost: 'text-brand hover:bg-brand-soft',
  danger: 'bg-st-rose text-white hover:brightness-95',
  success: 'bg-st-green text-white hover:brightness-95 shadow-sm',
  warning: 'bg-st-orange text-white hover:brightness-95',
};

export function Button({
  variant = 'primary',
  size = 'md',
  loading,
  icon,
  className,
  children,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; size?: 'sm' | 'md' | 'lg' | 'xl'; loading?: boolean; icon?: ReactNode }) {
  const sizes = { sm: 'min-h-10 px-3.5 text-base rounded-xl gap-1.5', md: 'min-h-12 px-5 text-lg rounded-xl gap-2', lg: 'min-h-14 px-6 text-xl rounded-2xl gap-2.5', xl: 'min-h-16 px-7 text-2xl rounded-2xl gap-3' };
  return (
    <button
      type="button"
      {...rest}
      disabled={rest.disabled || loading}
      className={cx('inline-flex select-none items-center justify-center font-display font-semibold transition active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50', sizes[size], VARIANT[variant], className)}
    >
      {loading ? <Loader2 className="size-5 animate-spin" /> : icon}
      {children}
    </button>
  );
}

// ---------------- การ์ด / หัวข้อ
export function Card({ children, className, title, action, padded = true }: { children: ReactNode; className?: string; title?: ReactNode; action?: ReactNode; padded?: boolean }) {
  return (
    <section className={cx('rounded-3xl border border-line bg-white', padded && 'p-5', className)}>
      {(title || action) && (
        <div className={cx('mb-4 flex flex-wrap items-center justify-between gap-3', !padded && 'px-5 pt-5')}>
          {title && <h2 className="text-xl font-bold">{title}</h2>}
          {action}
        </div>
      )}
      {children}
    </section>
  );
}

export function PageHeader({ title, subtitle, actions, back }: { title: ReactNode; subtitle?: ReactNode; actions?: ReactNode; back?: ReactNode }) {
  return (
    <header className="mb-5 flex flex-wrap items-end justify-between gap-3">
      <div className="min-w-0">
        {back}
        <h1 className="text-3xl font-bold md:text-4xl">{title}</h1>
        {subtitle && <p className="mt-1 text-lg text-muted">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
    </header>
  );
}

// ---------------- ป้าย
export function StageBadge({ stage, size = 'md' }: { stage: Stage; size?: 'sm' | 'md' | 'lg' }) {
  const info = STAGE_INFO[stage];
  const t = toneOf(info.color);
  const s = { sm: 'px-2.5 py-0.5 text-sm', md: 'px-3 py-1 text-base', lg: 'px-4 py-1.5 text-lg' }[size];
  return <span className={cx('inline-flex items-center gap-1.5 whitespace-nowrap rounded-full font-display font-semibold text-white', t.bg, s)}>{info.label}</span>;
}

export function HoldChip({ reason }: { reason: HoldReason }) {
  return <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-st-amber-soft px-3 py-0.5 text-base font-bold text-[#9a6b00]">⏸ {HOLD_INFO[reason]?.label ?? reason}</span>;
}

export function PriorityChip({ priority }: { priority: Priority }) {
  if (priority === 'normal' || priority === 'low') return null;
  return <span className={cx('rounded-full px-2.5 py-0.5 text-sm font-bold', priority === 'urgent' ? 'bg-st-rose text-white' : 'bg-st-orange-soft text-st-orange')}>{PRIORITY_INFO[priority].label}</span>;
}

export function Chip({ children, className }: { children: ReactNode; className?: string }) {
  return <span className={cx('inline-flex items-center gap-1 rounded-full bg-st-slate-soft px-3 py-0.5 text-base text-ink', className)}>{children}</span>;
}

// ---------------- ช่องกรอก
export function Field({ label, hint, error, children, required }: { label: ReactNode; hint?: ReactNode; error?: string | null; children: ReactNode; required?: boolean }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-lg font-semibold">
        {label}
        {required && <span className="text-st-rose"> *</span>}
      </span>
      {children}
      {hint && !error && <span className="mt-1 block text-base text-muted">{hint}</span>}
      {error && <span className="mt-1 block text-base font-medium text-st-rose">{error}</span>}
    </label>
  );
}

const inputCls = 'w-full rounded-xl border-2 border-line bg-white px-4 py-3 text-lg placeholder:text-muted/60 focus:border-brand focus:outline-none disabled:bg-st-slate-soft';

/** type="date" → ช่อง dd/mm/yyyy, type="time" → ช่อง 24 ชม. (ค่าที่ได้ยังเป็น YYYY-MM-DD / HH:mm) */
export function Input(props: InputHTMLAttributes<HTMLInputElement>) {
  if (props.type === 'date' || props.type === 'time') {
    const { type, onChange, ...rest } = props;
    const handler = onChange as unknown as ((e: { target: { value: string } }) => void) | undefined;
    return type === 'date' ? <DateInput {...rest} onChange={handler} /> : <TimeInput {...rest} onChange={handler} />;
  }
  return <input {...props} className={cx(inputCls, props.className)} />;
}
export function Textarea(props: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea rows={3} {...props} className={cx(inputCls, 'leading-relaxed', props.className)} />;
}
export function Select({ children, ...props }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select {...props} className={cx(inputCls, 'appearance-auto', props.className)}>
      {children}
    </select>
  );
}

/** ปุ่มเลือกแบบชิป (เลือกได้หลายอัน) — ใช้แทน checkbox เล็ก ๆ */
export function ChipPicker({ options, value, onChange }: { options: string[]; value: string[]; onChange: (v: string[]) => void }) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((o) => {
        const on = value.includes(o);
        return (
          <button
            key={o}
            type="button"
            onClick={() => onChange(on ? value.filter((x) => x !== o) : [...value, o])}
            className={cx('min-h-11 rounded-full border-2 px-4 text-base font-medium transition', on ? 'border-brand bg-brand text-white' : 'border-line bg-white hover:border-brand')}
          >
            {on ? '✓ ' : ''}
            {o}
          </button>
        );
      })}
    </div>
  );
}

/** ชิปเลือกได้หลายอัน + ช่อง "อื่น ๆ" พิมพ์เพิ่มเองได้ (ค่าที่พิมพ์เพิ่มแสดงเป็นชิปที่เลือกไว้) */
export function ChipPickerOther({ options, value, onChange, placeholder = 'อื่น ๆ (พิมพ์เพิ่ม)' }: { options: string[]; value: string[]; onChange: (v: string[]) => void; placeholder?: string }) {
  const [text, setText] = useState('');
  const extra = value.filter((v) => !options.includes(v));
  const add = () => {
    const t = text.trim();
    if (t && !value.includes(t)) onChange([...value, t]);
    setText('');
  };
  return (
    <div className="flex flex-col gap-2">
      <ChipPicker options={[...options, ...extra]} value={value} onChange={onChange} />
      <div className="flex gap-2">
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              add();
            }
          }}
          placeholder={placeholder}
          className={cx(inputCls, 'py-2.5')}
        />
        <Button variant="secondary" onClick={add} disabled={!text.trim()}>
          เพิ่ม
        </Button>
      </div>
    </div>
  );
}

/** ปุ่มเลือกอันเดียวแบบใหญ่ */
export function Segmented<T extends string>({ options, value, onChange }: { options: { value: T; label: ReactNode }[]; value: T; onChange: (v: T) => void }) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          className={cx('min-h-12 flex-1 rounded-xl border-2 px-4 text-lg font-semibold transition', value === o.value ? 'border-brand bg-brand-soft text-brand' : 'border-line bg-white hover:border-brand')}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

// ---------------- หน้าต่าง
export function Modal({ open, onClose, title, children, footer, wide }: { open: boolean; onClose: () => void; title: ReactNode; children: ReactNode; footer?: ReactNode; wide?: boolean }) {
  const ref = useRef<HTMLDialogElement>(null);
  const id = useId();
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    if (!open && d.open) d.close();
  }, [open]);
  return (
    <dialog
      ref={ref}
      aria-labelledby={id}
      onClose={onClose}
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      className={cx('m-auto w-[calc(100%-1.5rem)] rounded-3xl border-0 bg-white p-0 text-ink shadow-2xl backdrop:bg-ink/50', wide ? 'max-w-3xl' : 'max-w-lg')}
    >
      {open && (
        <div className="flex max-h-[88vh] flex-col">
          <div className="flex items-center justify-between gap-3 border-b border-line px-6 py-4">
            <h2 id={id} className="text-2xl font-bold">
              {title}
            </h2>
            <button type="button" onClick={onClose} className="grid size-11 place-items-center rounded-full hover:bg-st-slate-soft" aria-label="ปิด">
              <X className="size-6" />
            </button>
          </div>
          <div className="overflow-y-auto px-6 py-5">{children}</div>
          {footer && <div className="flex flex-wrap justify-end gap-2 border-t border-line px-6 py-4">{footer}</div>}
        </div>
      )}
    </dialog>
  );
}

// ---------------- สถานะว่าง / โหลด / ผิดพลาด
export function Spinner({ label = 'กำลังโหลด…' }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-3 py-16 text-xl text-muted">
      <Loader2 className="size-7 animate-spin text-brand" /> {label}
    </div>
  );
}

export function Empty({ icon, title, children }: { icon?: ReactNode; title: string; children?: ReactNode }) {
  return (
    <div className="flex flex-col items-center gap-3 rounded-3xl border-2 border-dashed border-line bg-white/60 px-6 py-12 text-center">
      {icon && <div className="text-brand">{icon}</div>}
      <p className="text-xl font-semibold">{title}</p>
      {children && <div className="text-lg text-muted">{children}</div>}
    </div>
  );
}

export function ErrorBox({ error }: { error: unknown }) {
  return <div className="rounded-2xl bg-st-rose-soft px-5 py-4 text-lg font-medium text-st-rose">{error instanceof Error ? error.message : 'เกิดข้อผิดพลาด'}</div>;
}

export function Stat({ label, value, tone = 'blue', onClick, hint }: { label: ReactNode; value: ReactNode; tone?: string; onClick?: () => void; hint?: ReactNode }) {
  const t = toneOf(tone);
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      className={cx('flex flex-col items-start gap-1 rounded-2xl p-4 text-left transition', t.soft, onClick && 'hover:brightness-95 active:scale-[0.99]')}
    >
      <span className="text-base font-semibold text-ink/80">{label}</span>
      <span className={cx('tabular font-display text-4xl font-bold leading-none', t.text)}>{value}</span>
      {hint && <span className="text-sm text-muted">{hint}</span>}
    </Tag>
  );
}
